import type { JSONContent } from '@tiptap/core'
import { GitCompareArrows } from 'lucide-react'
import { createTiptapDiff } from '@/utils/tiptap-diff'
import { ScrollArea } from '@deepguide-ai/ui/scroll-area'
import { Skeleton } from '@deepguide-ai/ui/skeleton'
import { PolicyContentViewer } from '@/components/screenata/PolicyContentViewer'
import { createInlineAnnotatedDocument } from './tiptapDiffAnnotators'

type TiptapInlineDiffViewProps = {
  previous: JSONContent | null
  current: JSONContent
  previousLabel: string
  currentLabel: string
}

export function TiptapInlineDiffView({
  previous,
  current,
  previousLabel,
  currentLabel,
}: TiptapInlineDiffViewProps) {
  if (!previous) {
    return (
      <div className='flex min-h-[260px] flex-1 items-center justify-center px-6 text-center'>
        <div>
          <GitCompareArrows className='mx-auto h-5 w-5 text-text-placeholder' />
          <p className='mt-2 text-[13px] font-medium text-text-secondary'>
            Please choose version
          </p>
          <p className='mt-1 text-[12px] text-text-subtle'>
            Select a version above to load the inline comparison.
          </p>
        </div>
      </div>
    )
  }

  const diff = createTiptapDiff(previous, current)
  if (!diff.hasChanges) {
    return (
      <div className='flex min-h-[180px] flex-1 items-center justify-center px-6 text-center'>
        <div>
          <GitCompareArrows className='mx-auto h-5 w-5 text-text-placeholder' />
          <p className='mt-2 text-[13px] font-medium text-text-secondary'>
            No changes between {previousLabel} and {currentLabel}.
          </p>
        </div>
      </div>
    )
  }

  const annotated = createInlineAnnotatedDocument(previous, current)

  return (
    <div className='min-h-0 flex-1 overflow-x-auto'>
      <ScrollArea className='h-full min-w-[760px]'>
        <div className='min-h-full bg-card'>
          <div className='border-b border-border bg-[#F8FAFC] px-4 py-2 text-[12px] font-semibold text-text-secondary dark:bg-surface-subtle'>
            Inline changes {previousLabel} {'->'} {currentLabel}
          </div>
          <div className='mx-auto max-w-[860px] px-8 py-6 [&_.ProseMirror]:max-w-none [&_.ProseMirror]:text-[13px] [&_.ProseMirror]:leading-6 [&_.editor-container]:pt-0 [&_.editor-content]:rounded-none'>
            <PolicyContentViewer
              contentKey={`compare-inline-${previousLabel}-${currentLabel}`}
              plateContent={annotated}
              readOnly
              reviewMode={false}
              enableComments={false}
              className='h-full'
              editorClassName='mt-0 mb-0'
            />
          </div>
        </div>
      </ScrollArea>
    </div>
  )
}

export function TiptapInlineLoadingView() {
  return (
    <div className='min-h-0 flex-1 overflow-x-auto'>
      <ScrollArea className='h-full min-w-[760px]'>
        <div className='min-h-full bg-card'>
          <div className='border-b border-border bg-[#F8FAFC] px-4 py-2 text-[12px] font-semibold text-text-subtle dark:bg-surface-subtle'>
            Inline changes
          </div>
          <div className='mx-auto max-w-[860px] px-8 py-6'>
            <div className='space-y-8'>
              <div className='space-y-4'>
                <Skeleton className='h-8 w-3/4 rounded-sm' />
                <div className='space-y-2'>
                  <Skeleton className='h-4 w-2/5 rounded-sm' />
                  <Skeleton className='h-4 w-1/2 rounded-sm' />
                  <Skeleton className='h-4 w-5/12 rounded-sm' />
                  <Skeleton className='h-4 w-7/12 rounded-sm' />
                </div>
              </div>
              <div className='space-y-3'>
                <Skeleton className='h-6 w-1/3 rounded-sm' />
                <Skeleton className='h-4 w-full rounded-sm' />
                <Skeleton className='h-4 w-11/12 rounded-sm' />
                <Skeleton className='h-4 w-10/12 rounded-sm' />
                <Skeleton className='h-4 w-3/4 rounded-sm' />
              </div>
              <div className='space-y-3'>
                <Skeleton className='h-6 w-2/5 rounded-sm' />
                <Skeleton className='h-4 w-full rounded-sm' />
                <Skeleton className='h-4 w-9/12 rounded-sm' />
                <Skeleton className='h-4 w-11/12 rounded-sm' />
              </div>
            </div>
          </div>
        </div>
      </ScrollArea>
    </div>
  )
}
